import { v4 as uuidv4 } from 'uuid'
import ArticleCollection, { getModifier } from '../catalog/ArticleCollection'
import { i18nLabelInterface, variationType } from '../catalog/catalog'
import Table, { ElementVariationsInterface } from '../data/Table'
import Collider2D from '../lib/Collider2D'
import { baseAttribute, baseAttributes, catalogAttribute } from './composition'
import Group from './Group'
import Composition from './Composition'
import { DomainModelData } from "./composition";
import Ruleset from '../rules/Ruleset'
import Toggles from '../toggles/Toggles'
import Light, { VibiaLightProps } from './Light'
import "./../rules/RuleHelper"
import DomainElementsFilter from '@domain/utils/DomainElementsFilter'
import { instanceofType } from '@domain/utils/utils'
import Categorization from '@domain/utils/Categorization'
import { hexDictionary } from '@domain/data/Constants'
import type { Action } from '@domain/rules/Action'

type AttributeVariationType = AttributeVariationsType[number]

type AttributeVariationsType = VibiaLightProps['attributeOptions']['controls'] |
	VibiaLightProps['attributeOptions']['temperatures'] |
	VibiaLightProps['attributeOptions']['finishes'] |
	VibiaLightProps['attributeOptions']['installations']

export function parseDomainData(object: DomainModelData) {
	if (!object || !object.model) return null

	const {
		subfamily,
		catalog,
		familyName,
		applicationLocation,
		technicalData,
		controls,
		temperatures,
		finishes,
		installations,
		compositor,
		textSalesData,
		pendantHeights
	} = object

	const finishesWithHex = finishes.map(finish => {
		if (!finish.hex)
			finish.hex = hexDictionary[finish.modifier]

		return finish
	})

	const parsedData = {
		info: {
			subfamily,
			catalog,
			familyName,
			applicationLocation
		},
		technicalData,
		textSalesData,
		attributeOptions: {
			controls, //: controls.map(parseVariation('control')),
			temperatures, //: temperatures.map(parseVariation('temperature')),
			finishes: finishesWithHex, //: finishes.map(parseVariation('color')),
			installations, //: installations.map(parseVariation('installation'))
			pendantHeight: pendantHeights?.[0]
		},
		renderAttributes: compositor
	}

	return parsedData
}

function getAttributesCurrentlyValid(lightData, instance) {
	let lightDataResult = lightData;

	if (lightData && lightData.attributeOptions) {
		let attributeOptions = lightData.attributeOptions
		let temperatures = attributeOptions?.temperatures ?? []
		let installations = attributeOptions?.installations ?? []
		let controls = attributeOptions?.controls ?? []
		let finishes = attributeOptions?.finishes ?? []

		temperatures = temperatures.filter(temperature => instance.isAttributeCurrentlyValid(temperature))
		installations = installations.filter(installation => instance.isAttributeCurrentlyValid(installation))
		finishes = finishes.filter(finish => instance.isAttributeCurrentlyValid(finish))
		controls = controls.filter(control => instance.isAttributeCurrentlyValid(control))
		attributeOptions = { ...attributeOptions, controls, finishes, installations, temperatures }
		lightDataResult = { ...lightData, attributeOptions }
	}

	return lightDataResult
}

const deepCopy = obj => JSON.parse(JSON.stringify(obj)) // change to structuredClone when node is updated to v17

export default class Base {
	_objectId: string
	parent: Group | undefined
	props: any
	size: {
		width: number,
		height: number,
		depth: number
	}
	data: DomainModelData
	ruleset: Ruleset
	elementOrderValue: Number;
	wayOfJson = false;
	createdAt: Number
	static className = 'Base'
	type = 'Base';
	constructor(parent?, id?, attributes?: baseAttributes, data?: DomainModelData) {
		this._objectId = id ?? uuidv4()
		this.parent = parent
		this.data = data
		this.props = {
			attributes: {}
		}
		this.props.elementOrderValue = 1
		this.createdAt = Date.now()

		if (this.getParent()) {
			this.getParent().tagByCreationDate()
		}

		this.ruleset = new Ruleset({ rules: data?.rules ? data.rules : [] }, this)

		if (attributes) {
			Object.keys(attributes).forEach((attrName: baseAttribute) => this.setAttribute(attrName, attributes[attrName], true))
		}

		let elemData = parseDomainData(data)
		elemData = getAttributesCurrentlyValid(elemData, this)

		if (elemData) this.wayOfJson = true

		this.props = deepCopy({
			...this.props,
			...(elemData ?? {})
		})
		try {
			if (!this.getAttribute('pendantHeight')) {
				const pendantHeight = this.getDefaultAttributeValue('pendantHeight')

				if (pendantHeight) {
					this.setAttribute('pendantHeight', pendantHeight)
				}
			}
		} catch (error) { }
	}


	protected getProps() {
		return this.props
	}

	getType() {
		return this.type
	}

	instanceofType(type: string) {
		return instanceofType(this, type)
	}

	getClassName() {
		// @ts-ignore
		return this.constructor.className
	}

	getArticleCollection(): ArticleCollection {
		return this.parent?.getArticleCollection()
	}

	getLights(): any {
		throw 'Base has no lights'
	}

	getComplements(): any {
		return []
	}

	getElementOrderValue() {
		return this.props.elementOrderValue
	}

	getElementCreatedTimeStamp() {
		return this.createdAt
	}

	setElementOrderValue(newElementOrderValue: Number) {
		this.props.elementOrderValue = newElementOrderValue
		return this
	}

	reload() {
		this.getParent() ? this.getParent().reload() : null
		return this
	}

	setDeltaAttribute(attribute: baseAttribute, value: any, own?, strict?) {
		return this.setAttribute(attribute, this.getAttribute(attribute) + value, own)
	}

	getCatalogAttributeKeys(): catalogAttribute[] {
		return []
	}

	getCatalog() {
		return this?.props?.info?.catalog
	}

	getVariationsMOQ(attribute: catalogAttribute): number {
		let MOQ = 0
		const variations = this.getAttributeVariations(attribute)
		variations.forEach(v => {
			if (v.MOQ > MOQ) MOQ = v.MOQ
		})
		return MOQ
	}

	getTechnicalInformation(): Record<string, any> {
		const finishes = this.getAttributeVariations("color")
		const controls = this.getAttributeVariations("control")
		const temperatures = this.getAttributeVariations("temperature")
		const installations = this.getAttributeVariations("installation")
		const cri = this.getTechnicalDataByKey("cri")
		const materials = this.getTechnicalDataByKey("materials")
		const MOQ = this.getVariationsMOQ("color")
		const lumens = cri.lumensLed
		const packaging = this.getTechnicalDataByKey('packaging')
		const photometry = this.getTechnicalDataByKey('photometry')
		const certificates = this.getTechnicalDataByKey('certificates')
		const attachments: DomainModelData['technicalData']['attachments'] = this.getTechnicalDataByKey('attachments')
		const toolboxContent: DomainModelData['technicalData']['toolboxContent'] = this.getTechnicalDataByKey('toolboxContent')

		return {
			finishes,
			controls,
			temperatures,
			installations,
			cri: [cri],
			materials,
			MOQ,
			lumens,
			packaging,
			photometry,
			certificates,
			attachments,
			toolboxContent
		}
	}

	getAttributeKeys() {
		return Object.keys(this.getAttributes())
	}

	setAttribute(attribute: baseAttribute, value: any, own?) {
		const tmpAttribute = this.props.attributes[attribute]
		this.props.attributes[attribute] = this.requestSetAttribute(attribute, value, this)
		try {
			this.requestValidate();
		} catch (e) {
			this.props.attributes[attribute] = tmpAttribute
		}
		return this
	}

	setApplicationLocationId(applicationLocationId) {
		this.props.applicationLocationId = applicationLocationId
	}

	getApplicationLocationId() {
		return this.props.applicationLocationId
	}

	getProductTypology(): string | null {
		return null;
	}

	getSpecificProductTypology() {
		const type1 = this.data?.articleType?.level1
		const type2 = this.data?.articleType?.level2
		const type3 = this.data?.articleType?.level3

		return type3 ?? type2 ?? type1 ?? null
	}

	getProductTypologies() {
		let result = null
		if (this.data?.articleType) {
			result = { ...this.data?.articleType }
			if (!result.level1) {
				result.level1 = { ...result }
			}
		}

		return result;
	}

	//validate the element and its childrens
	validate() {
		if (Toggles.isEnabled('rules')) {
			this.ruleset.evaluate()
		}
	}

	evaluateActions(): Array<Action> {
		if (this.ruleset) {
			return this.ruleset.evaluateActionRules()
		}
		return []
	}

	//sends a request to the parent to start a validation process.
	requestValidate() {
		if (this.getParent()) {
			//look for a parent until the request is accepted.
			//it will only be accepted by the top node
			this.getParent().requestValidate()
		} else {
			//i am the top node, i can fullfill this validate request
			this.validate()
		}
	}

	getDefaultAttributeValue(attribute: baseAttribute) {
		const articleCollection = this.getArticleCollection()
		let defaultValue = Table.getDefaultAttributeValue(this.getModel(), attribute, articleCollection)

		if (attribute == 'pendantHeight') {
			const pendantHeightOptions = this.getAttributeVariations('pendantHeight')
			if (pendantHeightOptions?.standard)
				defaultValue = pendantHeightOptions.standard
		}
		return defaultValue
	}

	resetAttribute(attribute: baseAttribute) {
		const defaultValue = this.getDefaultAttributeValue(attribute)
		if (defaultValue) {
			this.setAttribute(attribute, defaultValue)
		}
		return this
	}

	getCompositions(): Composition[] {
		return []
	}

	isAttributeRemovable(attribute: baseAttribute): boolean {
		return true
	}

	deleteAttribute(attribute: baseAttribute, own?: boolean) {
		if (this.isAttributeRemovable(attribute)) {
			delete this.props.attributes[attribute]
		}
		return this
	}

	getInvalidAttributes(): catalogAttribute[] {
		const catalogAttributes = this.getCatalogAttributeKeys()
		const invalidAttributes = catalogAttributes.filter(attribute => {
			const attributeValue = this.getAttribute(attribute, true)
			if (!attributeValue) return false
			return !this.isNewAttributeValid(attribute, attributeValue)
		})

		return invalidAttributes
	}

	deleteInvalidAttributes(own?: boolean) {
		return this
	}

	resetInvalidAttributes(own?: boolean) {
		return this
	}

	isNewAttributeValid(attribute: string, newValue): boolean {
		const ownValid = true
		const parentsDecision = this.requestSetAttribute(attribute as baseAttribute, newValue, this)
		const validForParent = parentsDecision == newValue
		return ownValid && validForParent
	}

	getAttributes(withModifier?: boolean): baseAttributes {
		let attributes = JSON.parse(JSON.stringify(this.props.attributes))
		Object.keys(attributes).forEach(attribute => {
			if (attribute != 'color' && !withModifier && typeof attributes[attribute] == 'string' && getModifier(attributes[attribute])?.length <= 2) // FIXME -> attributes split temporalmente hasta asegurarnos que todos los atributos se setean bien dónde antes no se hacia
				attributes[attribute] = attributes[attribute].split(' ').slice(1).join(' ') // Elimina el modifier del atributo (Para que el usuario no lo vea)
		})
		return attributes
	}

	getAttribute(key: baseAttribute, withModifier?: boolean) {
		let attribute = this.props.attributes[key]
		if (attribute && key != 'color' && !withModifier && typeof attribute == 'string' && getModifier(attribute)?.length <= 2) // FIXME -> attributes split temporalmente hasta asegurarnos que todos los atributos se setean bien dónde antes no se hacia
			attribute = attribute.split(' ').slice(1).join(' ') // Elimina el modifier del atributo (Para que el usuario no lo vea)
		return attribute
	}

	getAttributeModifier(key: baseAttribute) {
		return getModifier(this.getAttribute(key, true))
	}

	getInstance() {
		return this
	}

	getOrigin(): string {
		return 'center'
	}

	setParent(parent) {
		this.parent = parent
	}

	getParent() {
		return this.parent
	}

	getParentByName(parentName) {
		if (!this?.getParent()) return null

		return this?.getParent().getClassName() == parentName
			? this?.getParent()
			: this?.getParent().getParentByName(parentName)
	}

	getId() {
		return this._objectId
	}

	setId(id) {
		this._objectId = id
		return this
	}

	regenerateId() {
		const newId = uuidv4()
		this.setId(newId)
		return this
	}

	getInstallation(withModifier?: boolean): any {
		throw 'No installation for this element'
	}

	getApplicationLocation() {
		const appLocation = this.props.info?.applicationLocation ?? []
		const result = appLocation.map(elem => ({ ...elem, id: Number(elem.id) }))
		return result
	}

	getModel(): any {
		return null
	}

	getCode() {
		return this.getId()
	}

	/**
	 * Whether the given installation modifier (by default, the selected one) is appended to the
	 * reference as `_<installation>`. Same rule `getComputedReference` applies, exposed so callers
	 * (e.g. the professional-area BFF) can ask it per installation option instead of copying it.
	 */
	hasInstallationInReference(installation: string = this.getAttributeModifier('installation')): boolean {
		if (!installation) return false
		const installations = this.getAttributeVariations("installation")
		if (Toggles.isEnabled("showInstallationWithAtLeastOneVariant")) {
			const atLeastOneInstallationVariation = installations.length >= 1
			return atLeastOneInstallationVariation && installation !== '00'
		}
		const applicationLocations = this.getApplicationLocation()
		const isOnlyHanging = applicationLocations.length === 1 && applicationLocations[0].id === 4
		const hasManyInstallations = installations.length > 1
		const hasSurfaceInstallation = installation == '10'
		return (hasSurfaceInstallation && isOnlyHanging && hasManyInstallations) || (!hasSurfaceInstallation && installation != '00')
	}

	getComputedReference() {
		// Reference -> Model + finish (2) + / o - + temperature (1) + dimming (1) + _ + installation (2) 

		const model = this.getModel()

		const finish = this.getAttributeModifier('color')
		const temperature = this.getAttributeModifier('temperature')
		const dimming = this.getAttributeModifier('control')
		const installation = this.getAttributeModifier('installation')

		if (!model || !finish || !temperature || !dimming || !installation)
			return null

		let reference16 = model + finish + '/' + temperature + dimming
		let reference21 = reference16.substring(0, 4) + '-' + reference16.substring(4)

		if (this.hasInstallationInReference(installation)) {
			reference16 = reference16 + '_' + installation
		}

		reference21 = reference16.substring(0, 4) + '-' + reference16.substring(4)
		return {
			reference16,
			reference21
		}
	}

	isAttributeCurrentlyValid(variation: AttributeVariationType) {
		const { fromValidityDate, toValidityDate } = variation
		if (fromValidityDate == '' || toValidityDate == '' || fromValidityDate == null || toValidityDate == null) return true

		const [day, month, year] = fromValidityDate.split('/')
		const [_day, _month, _year] = toValidityDate.split('/')
		const fromValue = new Date(`${month}/${day}/${year}`).getTime()
		const nowValue = new Date().getTime()
		const toValue = new Date(`${_month}/${_day}/${_year}`).getTime()
		const isBetweenDates = nowValue > fromValue && nowValue < toValue

		return isBetweenDates
	}

	getSize(_total?) {
		return this.size
	}

	setSize(size) {
		this.size = size
	}

	getColliders(position: number[]) {
		return [new Collider2D({ dimensions: { width: this.getSize().width, height: this.getSize().depth }, position: { x: position[0], y: position[2] } })]
	}

	getOrbitTarget(absolute) {
		return absolute ? this.getAbsolutePosition() : [0, 0, 0]
	}

	getBoundingBox(total?) {
		return {
			...this.getSize(total),
			x: 0,
			y: parseFloat(((this.getSize(total)?.height / 2) * -1 || 0).toFixed(4)),
			z: 0
		}
	}

	//this is the setAttribute from childrens
	requestSetAttribute(attribute: baseAttribute, value, element?: Base) {
		if (this.parent) {
			return this.parent.requestSetAttribute(attribute, value, element ?? this)
		} else {
			return value
		}
	}

	move(deltaX, deltaY, deltaZ, force?): Base {
		let returnedElement: Base = this
		if (force) {
			const composableElement = this.getParent().getElement(this)
			composableElement.options.position[0] = deltaX
			composableElement.options.position[1] = deltaY
			composableElement.options.position[2] = deltaZ

			returnedElement = this.move(0, 0, 0)
		} else {
			returnedElement = this.getParent().moveElement(this, deltaX, deltaY, deltaZ)
		}

		return Toggles.isEnabled("moveDomainMethodReturnsMovedElement") ? returnedElement : this
	}

	moveElement(element: Base, x, y, z) {
		return this.move(x, y, z)
	}

	moveElements(x, y, z) {
		return this.move(x, y, z)
	}

	rotate2D(rotationCenterX, rotationCenterZ, angle): this {
		this.getParent().rotate2DElement(this, rotationCenterX, rotationCenterZ, angle)
		return this
	}

	rotate2DElement(element: Base, rotationCenterX, rotationCenterZ, angle) {
		return this.rotate2D(rotationCenterX, rotationCenterZ, angle)
	}

	rotate2DElements(rotationCenterX, rotationCenterZ, angle): this {
		return this.rotate2D(rotationCenterX, rotationCenterZ, angle)
	}

	getElement(element?) {
		return this.getParent().getElement(element ?? this)
	}

	getElements() {
		return [];
	}

	getAllElements(excludeAutoadded: boolean = false): Array<Base> {
		return [this]
	}

	getAllElementsByType(type: string, excludeAutoadded: boolean = false): Array<Base> {
		const elements = this.getAllElements(excludeAutoadded)
		const elementsByType = DomainElementsFilter.applyFilter(elements, 'type', type)
	
		if (excludeAutoadded && Toggles.isEnabled('hideAutoAddedAccesories')) {
			return elementsByType.filter(element => !element.isAutoadded())
		}
		
		return elementsByType
	}

	getAllElementsByTypes(types: Array<string>, excludeAutoadded: boolean = false): Array<Base> {
		const elements: Array<Base> = []
		types.forEach(type => elements.push(...this.getAllElementsByType(type, excludeAutoadded)))
		const result = Array.from(new Set(elements))
		return result
	}

	groupElementsByType(types: Array<string>, other?: boolean, typesToExclude?: Array<string>) {
		const result: Record<string, Array<Base>> = {}

		const isElementTypeExcluded = (element: Base) => {
			if (!typesToExclude) return false

			const hasExcludedType = typesToExclude.some(typeToExclude => instanceofType(element, typeToExclude))
			return hasExcludedType
		}

		types.forEach(type => {
			const elementsByType = this.getAllElementsByType(type)
			const validElementsByType = elementsByType.filter(element => !isElementTypeExcluded(element)) // exclude <typesToExclude> elements
			result[type] = validElementsByType
		})

		if (other) {
			result.other = []

			const allElements = this.getAllElements()
			allElements
				.filter(element => !isElementTypeExcluded(element)) // exclude <typesToExclude> elements
				.filter(element => { // exclude elements in other categories
					const otherCategoriesElements = Object.values(result).flat()
					const isElementInAnotherCategory = otherCategoriesElements.includes(element)
					return !isElementInAnotherCategory
				})
				.forEach(element => result.other.push(element))
		}

		return result
	}

	groupArticlesByReference() {
		const articleTypes = ["Light", "Complement"]
		const articles = this.getAllElementsByTypes(articleTypes)

		const groupsByReference: Record<string, Array<Base>> = {};
		const nonGroupedElements: Array<Array<Base>> = []

		articles.forEach(article => {
			const references = article.getComputedReference();
			const reference = references?.reference16

			if (!reference) {
				nonGroupedElements.push([article])
				return // Next iteration
			}

			if (!groupsByReference[reference]) {
				groupsByReference[reference] = [];
			}
			groupsByReference[reference].push(article);
		});

		const result = [...Object.values(groupsByReference), ...nonGroupedElements]
		return result;
	}

	groupArticlesByAttributes() {
		const articleTypes = ["Light", "Complement"];
		const articles = this.getAllElementsByTypes(articleTypes);

		const groupsByAttributes: Record<string, Array<Base>> = {};

		articles.forEach(article => {

			const attributes = {
				model: article.getModel(),
				color: article.getAttribute('color'),
				control: article.getAttribute('control'),
				temperature: article.getAttribute('temperature'),
				installation: article.getAttribute('installation'),
				pendantHeight: article.getAttribute('pendantHeight')
			};

			const attributesKey = JSON.stringify(attributes);

			if (!groupsByAttributes[attributesKey]) {
				groupsByAttributes[attributesKey] = [];
			}
			groupsByAttributes[attributesKey].push(article);
		});

		const result = Object.values(groupsByAttributes);

		return result;
	}



	categorizeLightsByLightingEffect() {
		const lightsToCategorize = this.getAllElementsByType("Light") as Light[];
		return Categorization.categorizeLightsByLightingEffect(lightsToCategorize)
	}

	categorizeLightsByLuminaryShape() {
		const lightsToCategorize = this.getAllElementsByType("Light") as Light[];
		return Categorization.categorizeLightsByLuminaryShape(lightsToCategorize)
	}

	getInfo() {
		const lumens = this.getTechnicalDataByKey('lumens')?.value;
		const watts = this.getTechnicalDataByKey('watts')?.value;
		const netWeight = this.getTechnicalDataByKey('packaging')?.netWeight ?? 0;
		const height = this.getSize(true)?.height;
		const attributes = this.getAttributes(false)
		const references = this.getComputedReference()
		const model = this.getModel()
		const article = {
			reference: references?.reference16,
			reference_16: references?.reference16,
			reference_21: references?.reference21,
			model,
			...attributes
		}

		const delivered_power = 0
		const consumed_power = 0

		return {
			lumens,
			watts,
			height,
			attributes,
			article,
			netWeight,
			delivered_power,
			consumed_power,
			type: 'base'
		}
	}

	getAbsolutePosition(child?): number[] {
		return this.getParent() ? this.getParent().getAbsolutePosition(child ?? this) : [0, 0, 0]
	}

	getLightOptions() {
		throw 'Cannot get Light options of this element'
	}

	replace(element: Base) {
		if(element !== this){
			const parent = this.getParent() as Group
			const options = parent.getElement(this).options
			element.setParent(parent)
			parent.props.elements.push({
				element: element,
				options: options
			})
			parent.remove(this)
		}
		return element
	}

	clone(mode: string = 'detached', articleCollection?: ArticleCollection) {
		switch (mode) {
			// Clones the element and gives it cloned ancestors that mimic the original ones
			case 'recursive':
				let _articleCollection = this.getArticleCollection()
				const originalId = this.getId()
				const element = this.clone('newId', _articleCollection)
				let childClone = element;
				let parent = this.getParent();
				while (parent) {
					let parentClone = parent.clone('newId', _articleCollection)
					const originalElement = parentClone.getElement(originalId)
					if (originalElement) {
						parentClone.remove(originalElement.element)
					}
					parentClone = parentClone.addElement({
						options: { position: [0, 0, 0] },
						...originalElement,
						element: childClone
					})
					childClone = parentClone;
					parent = parent.getParent()
				}
				return element
			case 'newId':
				// Clones the element without any parent (Creating a new ID for the new element)
				return this.fromJSON(
					JSON.parse(
						JSON.stringify({
							...this.toJSON(),
							_objectId: undefined
						})
					),
					{
						...articleCollection && { articleCollection }
					}
				)
			default: // 'detached'
				// Clones the element without any parent (Keeping ID)
				return this.fromJSON(
					JSON.parse(
						JSON.stringify(this.toJSON())
					),
					{
						...articleCollection && { articleCollection }
					}
				)
		}

	}

	toJSON(dump?): any {
		return {
			type: this.getClassName(),
			props: dump ? dump(this.props) : this.props,
			_objectId: this._objectId
		}
	}

	fromJSON(json, options?: any, instance?: Base) {
		return Base.fromJSON(json, options, instance)
	}

	static fromJSON(json, _options?: any, instance?: Base) {
		const base = instance ?? new Base()
		if (json?.size) {
			base.setSize(json.size)
		}
		if (json?._objectId) {
			base.setId(json._objectId)
		}

		if (json?.props?.attributes) {
			base.props.attributes = json.props.attributes
		}
		return base
	}

	static dataFactory(data?, children?: Base[]) {
		throw "Base cannot be instanced: " + this
	}

	static parseAttributeValueFromVariationOption(attribute: variationType, option: AttributeVariationType): string {
		let value = null
		switch (attribute) {
			case 'color':
				value = option?.description?.en
				break;
			case 'temperature':
			case 'control':
			case 'installation':
				value = option?.modifier + ' ' + option?.description?.en
				break;
		}

		return value
	}

	getAttributeVariation(attribute: catalogAttribute, modifier?: string): any {
		if (attribute == 'pendantHeight') {
			return this?.props?.attributeOptions?.pendantHeight ?? null
		}
		const attributeModifier = modifier ?? this.getAttributeModifier(attribute)
		const variations = this.getAttributeVariations(attribute)
		const variation = variations.find(elem => elem.modifier == attributeModifier)
		if (!variation) return null
		return variation
	}

	getAttributesVariations() {
		return this.props.attributeOptions
	}

	getAttributeDescriptions(attribute: catalogAttribute): i18nLabelInterface {
		const variation = this.getAttributeVariation(attribute)
		return variation?.description
	}

	getAttributeVariations(attribute: catalogAttribute): any {
		if (attribute == 'pendantHeight') {
			return this?.props?.attributeOptions?.pendantHeight ?? null
		}
		const attributesVariations = this.getAttributesVariations()
		const keyDictionary = {
			color: 'finishes',
			temperature: 'temperatures',
			control: 'controls',
			installation: 'installations'
		}
		const key = keyDictionary[attribute]

		return attributesVariations?.[key] ?? []
	}

	// DEPRECATED: use convertToPa or convertToPSA instead
	getSellableVariations(attribute: catalogAttribute, sellingMode: 'isIndividual') {
		if (Toggles.isEnabled('avoidPAsInToolkitListConfigurator')) {
			throw new Error('DEPRECATED METHOD: Use convertToPa or convertToPSA: avoidPAsInToolkitListConfigurator is enabled, getSellableVariations should not be used.')
		}
		try {
			const variations = this.getAttributeVariations(attribute);
			switch (sellingMode) {
				case 'isIndividual':
					return variations.filter(v => v.exclude_individual === 0);

				default:
					return [];
			}
		} catch (error) {
			return [];
		}
	}

	getOptionVariations(attribute: variationType) {
		let options = this.getAttributeVariations(attribute)
		const baseOption = options.find(variation => variation.type == 'default')

		return {
			options: options.filter(variation => variation.type == 'default' || variation.type == 'variation').map(variation => ({
				value: Base.parseAttributeValueFromVariationOption(attribute, variation),
				label: variation.description.en,
				multilangLabel: variation.description,
				compatible: {
					value: this.isAttributeCompatible(attribute, Base.parseAttributeValueFromVariationOption(attribute, variation)),
					reason: 'This combination is incompatible.'
				},
				...variation
			})),
			extraOptions: options.filter(variation => variation.type == 'optional').map(variation => ({
				value: Base.parseAttributeValueFromVariationOption(attribute, variation),
				label: variation.description.en,
				multilangLabel: variation.description,
				compatible: {
					value: this.isAttributeCompatible(attribute, Base.parseAttributeValueFromVariationOption(attribute, variation)),
					reason: 'This combination is incompatible.'
				},
				...variation
			})),
			// DV-6834: temporal variations, only present when a v3 call superposed them for an enabled user.
			temporalOptions: options.filter(variation => variation.type == 'temporal').map(variation => ({
				value: Base.parseAttributeValueFromVariationOption(attribute, variation),
				label: variation.description.en,
				multilangLabel: variation.description,
				compatible: {
					value: this.isAttributeCompatible(attribute, Base.parseAttributeValueFromVariationOption(attribute, variation)),
					reason: 'This combination is incompatible.'
				},
				...variation
			})),
			base: baseOption ? {
				value: Base.parseAttributeValueFromVariationOption(attribute, baseOption),
				label: baseOption.description.en,
				multilangLabel: baseOption.description,
				compatible: {
					value: this.isAttributeCompatible(attribute, Base.parseAttributeValueFromVariationOption(attribute, baseOption)),
					reason: 'This combination is incompatible.'
				},
				...baseOption
			} : null
		}
	}

	getElementOptions(): ElementVariationsInterface {
		let controlOptions = this.getAttributeVariations('control')
		const controls = controlOptions.map(variation => Base.parseAttributeValueFromVariationOption('control', variation))
		const controlsV2 = this.getOptionVariations('control')
		const colorsV2 = this.getOptionVariations('color')
		const extraColorsArray = this.getAttributeVariations('color').filter(variation => variation.type == 'optional')
		const extraColors = extraColorsArray.map(variation => Base.parseAttributeValueFromVariationOption('color', variation))
		const installationsV2 = this.getOptionVariations('installation')
		const temperatures = this.getAttributeVariations('temperature').map(variation => Base.parseAttributeValueFromVariationOption('temperature', variation))
		const temperaturesV2 = this.getOptionVariations('temperature')

		return {
			controls, controlsV2, colorsV2, extraColors, installationsV2, temperatures, temperaturesV2
		}
	}

	isAttributeCompatible(attribute, newValue) {
		const oldValue = this.getAttribute(attribute, true)
		if (oldValue == newValue) return true

		this.setAttribute(attribute, newValue)
		const changedValue = this.getAttribute(attribute, true)

		if (!oldValue) {
			this.deleteAttribute(attribute)
		} else {
			this.setAttribute(attribute, oldValue)
		}

		return oldValue != changedValue
	}

	isValidElementType(validElementTypes) {
		return validElementTypes.includes(this.getClassName())
	}

	attributeHasMultipleOptions(attribute) {
		return this.getAttributeVariations(attribute).length > 1
	}

	isComposingPendant() {
		return false
	}

	getTechnicalData() {
		return this.props.technicalData
	}

	getTechnicalDataByKey(key: keyof VibiaLightProps['technicalData']): any {
		const technicalData = this.getTechnicalData()
		return technicalData ? technicalData[key] : null

	}

	getTextSalesData() {
		return this.props.textSalesData
	}

	getTextSalesDataByKey(key: keyof VibiaLightProps['textSalesData']): any {
		const textSalesData = this.getTextSalesData()
		return textSalesData ? textSalesData[key] : null
	}

	isAutoadded(): any {
		return this.props.isAutoadded ?? false
	}

	isToolbox(): boolean {
		return false
	}

	setAutoadded(value: boolean): void {
		this.props.isAutoadded = value;
	}

	setRuleSet(ruleset: Ruleset): void {
		this.ruleset = ruleset;
	}

	setCompositionCompatibility(value: boolean): void {
		this.props.compositionCompatible = value;
	}

	isCompositionCompatible(): boolean {
		return this.props.compositionCompatible ?? true;
	}
}